import axios from 'axios';
import config from '../config/api';

const BASE_URL = config.endpoints.carnets;

export async function ensureFormatExists() {
  try {
    const res = await axios.get(`${BASE_URL}/formatocarnet/current`);
    const id = res?.data?.FORMATOCARNET_ID;
    if (!id) return { ok: false, reason: 'NO_FORMAT' };
    return { ok: true, id };
  } catch (error) {
    if (error?.response?.status === 404) {
      return { ok: false, reason: 'NO_FORMAT' };
    }
    return { ok: false, reason: 'ERROR', error };
  }
}

// Puerto backend para carnet y formato: 3000
// Otros servicios (inventario, hospital, etc.): 3001

export async function createFormat({ nombreDirectora, cedulaDirectora, logoFile }) {
  const formData = new FormData();
  formData.append('nombreDirectora', nombreDirectora || '');
  formData.append('cedulaDirectora', cedulaDirectora || '');
  formData.append('logo', logoFile);
  try {
    const res = await axios.post(`${BASE_URL}/formatocarnet`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return { success: true, data: res.data };
  } catch (error) {
    const status = error?.response?.status;
    // Si ya existe, hacemos update en lugar de fallar
    if (status === 409) {
      try {
        // Recuperar el formato actual
        const current = await axios.get(`${BASE_URL}/formatocarnet/current`);
        const id = current?.data?.FORMATOCARNET_ID;
        if (!id) return { success: false, status: 409, error: 'FORMAT_ALREADY_EXISTS' };

        // Preparar formData para PUT (multipart también)
        const upd = new FormData();
        if (nombreDirectora) upd.append('nombreDirectora', nombreDirectora);
        if (cedulaDirectora !== undefined) upd.append('cedulaDirectora', cedulaDirectora || '');
        if (logoFile) upd.append('logo', logoFile);

        const putRes = await axios.put(`${BASE_URL}/formatocarnet/${encodeURIComponent(id)}`, upd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        return { success: true, data: putRes.data };
      } catch (e) {
        // Devolver detalle del update fallido
        let msg = 'ERROR_UPDATING_FORMAT';
        try {
          if (e.response?.data) {
            if (typeof e.response.data === 'string') {
              try { msg = JSON.parse(e.response.data)?.message || msg; } catch (_) { void _; }
            } else if (e.response.data instanceof Blob) {
              const text = await e.response.data.text();
              try { msg = JSON.parse(text)?.message || msg; } catch (_) { void _; }
            } else if (typeof e.response.data === 'object') {
              msg = e.response.data.message || msg;
            }
          }
        } catch (_) { void _; }
        return { success: false, status: e?.response?.status, error: msg };
      }
    }

    // Parsear error genérico
    let message = 'ERROR_SAVING_FORMAT';
    try {
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          try { message = JSON.parse(error.response.data)?.message || message; } catch (_) { /* ignore */ }
        } else if (error.response.data instanceof Blob) {
          const text = await error.response.data.text();
          try { message = JSON.parse(text)?.message || message; } catch (_) { /* ignore */ }
        } else if (typeof error.response.data === 'object') {
          message = error.response.data.message || message;
        }
      }
    } catch (_) { /* ignore */ }
    return { success: false, status, error: message };
  }
}

export async function generateCarnetPdf(payload = {}) {
  const fmt = await ensureFormatExists();
  if (!fmt.ok) {
    return { success: false, error: fmt.reason === 'NO_FORMAT' ? 'NO_FORMAT' : 'ERROR' };
  }
  const url = `${BASE_URL}/carnet/${encodeURIComponent(fmt.id)}/generate`;
  try {
    const response = await axios.post(url, payload, { responseType: 'blob' });
    return { success: true, blob: response.data, headers: response.headers };
  } catch (error) {
    // Intentar extraer mensaje de error del backend
    let message = 'Error al generar carnet';
    try {
      if (error.response && error.response.data) {
        if (typeof error.response.data === 'string') {
          try { message = JSON.parse(error.response.data)?.message || message; } catch (_) { /* ignorar errores de parseo */ }
        } else if (error.response.data instanceof Blob) {
          const text = await error.response.data.text();
          try { message = JSON.parse(text)?.message || message; } catch (_) { /* ignorar errores de parseo */ }
        } else if (typeof error.response.data === 'object') {
          message = error.response.data.message || message;
        }
      }
    } catch (e) {
      // Ignorar errores de parseo
      void e;
    }
    return { success: false, error: message, status: error?.response?.status };
  }
}

export default {
  ensureFormatExists,
  createFormat,
  generateCarnetPdf,
};
